import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useRegistrationStreaming,
  useCaseStreaming,
  type RegistrationFilterModel,
} from '@actschurch/ccms-sdk';
import type { AnySourceConfig, Row, SourceKey } from '../types';

interface Args {
  sourceKey: SourceKey;
  source: AnySourceConfig;
  search: string;
}

interface Result {
  rows: Row[];
  loading: boolean;
  error: string | null;
  /** Stream progress, passed straight to <StreamProgress />. */
  progress: number;
  loadedCount: number;
}

/** Module-level so its identity never changes between renders. */
const NO_FILTER: Partial<RegistrationFilterModel> = {};

/**
 * Wraps the SDK streaming hooks for the streamed sources (registration, case).
 *
 * Now: `load()` is called exactly once per source switch (keyed on `sourceKey` only), and rows are
 * DERIVED from the stream's own `items` — no copy, no state, nothing for the effect to chase.
 *
 * Every filter except search is applied client-side in useReportTable: the stream returns the whole
 * set, so changing a filter must not restart it.
 */
export function useStreamSource({ sourceKey, source, search }: Args): Result {
  // Both hooks are called unconditionally to keep hook order stable. Neither loads on its own.
  const registrationStream = useRegistrationStreaming(NO_FILTER, 50);
  const caseStream = useCaseStreaming(undefined, 50);

  const active = source.mode !== 'streamed'
    ? null
    : sourceKey === 'registration'
      ? registrationStream
      : sourceKey === 'case'
        ? caseStream
        : null;
  const hasActive = active !== null;

  const [error, setError] = useState<string | null>(null);

  // A ref, not a dependency: even if the SDK hands back a new `load` function every render,
  // that can no longer re-trigger the effect.
  const loadRef = useRef<(() => Promise<unknown>) | undefined>(undefined);
  loadRef.current = active?.load as (() => Promise<unknown>) | undefined;

  useEffect(() => {
    setError(null);
    if (!hasActive) return;
    Promise.resolve(loadRef.current?.()).catch((e) => {
      console.error(`[${sourceKey}] stream failed:`, e);
      setError('Could not load records.');
    });
  }, [sourceKey, hasActive]);

  const items = active?.items;
  const rows = useMemo(() => (items ?? []) as unknown as Row[], [items]);

  // Client-side search over the streamed set, mirroring the list views' field lists.
  const searched = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const fields = sourceKey === 'registration'
      ? ['reference', 'description', 'courseName', 'locationName', 'leadFacilitatorName', 'statusName']
      : ['name', 'leaderName', 'needName', 'locationName', 'counseleeNames', 'statusName'];
    const idField = sourceKey === 'registration' ? 'registrationID' : 'caseID';
    return rows.filter((r) =>
      fields.some((f) => String(r[f] ?? '').toLowerCase().includes(term))
      || String(r[idField] ?? '').includes(term),
    );
  }, [rows, search, sourceKey]);

  return {
    rows: searched,
    loading: active?.isLoading ?? false,
    error,
    progress: active?.progress ?? 0,
    loadedCount: active?.loadedCount ?? 0,
  };
}